<template>
    <div id="checkout">
        <HeadFoot title="Checkout" v-on:back="back">
            <button
                type="button"
                class="btn text-gray-700 bg-white border border-gray-600 hover:bg-gray-600 hover:text-white refresh ml-2"
                v-if="app_env() === 'local' || app_env() === 'development'"
                @click="getCart()"
            >
                <font-awesome-icon icon="arrows-rotate" class="mr-1"></font-awesome-icon>
                Refresh
            </button>
        </HeadFoot>

        <div ref="checkout-main" v-if="!loading">
            <div class="container mx-auto pt-8 px-4 grid gap-2" v-show="!form">
                <div class="text-center" v-if="items && items.length < 1">
                    There are no items currently in your cart.
                </div>

                <div
                    v-for="(item, i) in items"
                    class="flex item-wrapper mb-3"
                    :class="{'border-t-2 border-leonard-grey pt-3': i > 0 && !item.parent_id}"
                    :key="item.id"
                >
                    <div class="flex-grow">
                        <div class="flex flex-row leading-none mb-2" v-if="!item.parent_id">
                            <p class="w-full text-gray-600">
                                <small>{{ getStudentName(item) }} - {{ item.access_code }}</small>
                            </p>
                        </div>

                        <div class="flex flex-col sm:flex-row items-start">
                            <figure
                                class="block pkg_img bg-gray-50 mb-4 sm:mr-6 sm:mb-0 flex-shrink-0"
                                :class="prefix + item.package"
                                v-if="!item.parent_id
                                    && !item.is_rush
                                    && !item.name.toUpperCase().split(' ').includes('RETOUCHING')
                                "
                            ></figure>

                            <div class="-mt-1 flex-grow" :class="{'w-full': item.parent_id || item.is_rush}">
                                <div
                                    class="flex flex-col sm:flex-row"
                                    :class="{
                                        'font-semibold text-lg': !item.parent_id,
                                        'text-gray-700': item.parent_id
                                    }"
                                >
                                    <div class="sm:w-3/4">{{ item.name }}</div>
                                    <div class="sm:w-1/4 text-gray-700 sm:text-gray-900 sm:text-right">
                                        ${{ item.price | formatMoney }}
                                    </div>
                                </div>

                                <div class="flex flex-row my-4 sm:my-0" v-if="item.description">
                                    <div class="sm:w-3/4 text-sm text-justify sm:text-left pr-4">
                                        <span>{{ item.description }}</span>
                                    </div>
                                </div>

                                <div class="flex flex-row">
                                    <div class="w-full">
                                        <ul
                                            class="flex flex-wrap flex-row mb-0"
                                            v-if="item.contents && item.contents.length > 0"
                                        >
                                            <li class="font-bold text-xs leading-loose text-leonard-green">
                                                Contents:
                                            </li>
                                            <li
                                                class="text-xs leading-loose ml-1"
                                                v-for="(name, i) in item.contents"
                                                :key="name"
                                            >
                                                {{ name }}
                                                <span v-if="i < item.contents.length - 1">,</span>
                                            </li>
                                        </ul>
                                    </div>
                                </div>

                                <div class="flex flex-row">
                                    <div class="w-full">
                                        <ul
                                            class="flex flex-wrap flex-row mb-0"
                                            v-if="item.poses && item.poses.length > 0 && !item.parent_id"
                                        >
                                            <li class="font-bold text-xs leading-loose text-leonard-green">
                                                <font-awesome-icon
                                                    :icon="['fas', 'circle-info']"
                                                    @click.prevent="showModal('poses', item.poses)"
                                                    class="mr-1"
                                                ></font-awesome-icon>
                                                Poses:
                                            </li>
                                            <li
                                                class="text-xs leading-loose ml-1"
                                                v-for="(pose, i) in item.poses"
                                                :key="pose"
                                            >
                                                {{ pose }}
                                                <span v-if="i < item.poses.length - 1">,</span>
                                            </li>
                                        </ul>
                                    </div>
                                </div>

                                <div
                                    class="flex flex-row pt-2"
                                    v-if="!item.parent_id"
                                >
                                    <div class="w-full">
                                        <button
                                            class="btn text-sm text-gray-700 border border-gray-600 hover:text-white hover:bg-red-700 hover:border-red-700"
                                            @click="removeItem(item, i)"
                                            :disabled="deleting"
                                            v-if="!disableForms"
                                        >
                                            Delete
                                            <font-awesome-icon
                                                class="ml-1"
                                                icon="trash"
                                                v-if="deleting !== i"
                                            ></font-awesome-icon>
                                            <font-awesome-icon
                                                class="ml-1"
                                                icon="spinner"
                                                v-if="deleting === i"
                                                spin
                                            ></font-awesome-icon>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="voucherAmt > 0" class="mt-3 mb-3">
                    <div class="flex font-semibold text-lg">
                        <div
                            class="w-3/4 text-left bg-leonard-green-transparent text-leonard-green"
                        >
                            <span class="p-1">VOUCHER</span>
                        </div>
                        <div
                            class="w-1/4 text-right bg-leonard-green-transparent text-leonard-green"
                        >
                            <span class="p-1">-${{ voucherAmt | formatMoney }}</span>
                        </div>
                    </div>
                </div>
                <div v-if="discount > 0" class="mt-3 mb-3">
                    <div class="flex font-semibold text-lg">
                        <div class="w-1/2 text-left bg-leonard-green-transparent text-leonard-green p-3">
                            DISCOUNT
                        </div>
                        <div class="w-1/2 text-right bg-leonard-green-transparent text-leonard-green p-3">
                            -${{ discount | formatMoney }}
                        </div>
                    </div>
                </div>
                <div class="grid mt-10">
                    <div
                        class="col-span-12 md:col-span-4 md:col-start-8 lg:col-span-2 lg:col-start-10"
                    >
                        <table class="float-right w-full" id="previewTable">
                            <tr>
                                <td><b>Subtotal:</b></td>
                                <td class="text-right">
                                    ${{ subtotal | formatMoney }}
                                </td>
                            </tr>
                            <tr v-if="discount > 0">
                                <td><b>Discount:</b></td>
                                <td class="text-leonard-green text-right">
                                    -${{ discount | formatMoney }}
                                </td>
                            </tr>
                            <tr v-if="voucherAmt > 0">
                                <td><b>Voucher Applied:</b></td>
                                <td class="text-leonard-green">
                                    -${{ voucherAmt | formatMoney }}
                                </td>
                            </tr>
                            <tr>
                                <td><b>S&H:</b></td>
                                <td class="text-right">
                                    ${{ shipping | formatMoney }}
                                </td>
                            </tr>
                            <tr>
                                <td><b>Tax:</b></td>
                                <td class="text-right">
                                    ${{ tax | formatMoney }}
                                </td>
                            </tr>
                            <tr>
                                <td><b>Total:</b></td>
                                <td class="text-right">
                                    ${{ total | formatMoney }}
                                </td>
                            </tr>
                        </table>
                    </div>
                </div>
            </div>

            <div class="container mx-auto pb-8 px-4 grid gap-2" v-if="items.length > 0" v-show="!form">
                <form
                    class="w-full max-w-xs md:max-w-md md:ml-auto my-6"
                    v-show="!disableRush"
                >
                    <div class="md:flex justify-end">
                        <label
                            class="inline-flex items-center text-gray-700 font-bold gap-2"
                            for="rush_processing"
                        >
                            <input
                                class="form-checkbox h-4 w-4 transition duration-150 ease-in-out disabled:bg-gray-200 mr-1"
                                v-model="rush"
                                id="rush_processing"
                                :disabled="disableForms || disableRush"
                                type="checkbox"
                                @change.prevent="toggleRushProcess"
                            />
                            <span class="text-sm">
                                Rush Processing
                            </span>
                        </label>
                    </div>
                </form>

                <Discount
                    ref="discountComponent"
                    :disabled="disableForms"
                    :subtotal="subtotal"
                    :isSenior="true"
                    :discount="discount"
                    :makePayment.sync="makePayment"
                    v-on:add="applyDiscount"
                    v-on:remove="removeDiscount"
                ></Discount>
            </div>
            <div
                class="bg-leonard-grey rounded-md grid md:grid-flow-row grid-cols-[1fr_auto_auto] gap-2 items-center my-4 p-4"
                v-if="items.length > 0"
                v-show="!form && makePayment"
            >
                <p v-if="!authorize_token && !internal && total === 0 && discount === 0 && !isEmployee">
                    Unable to checkout. Please refresh the page to try again.
                </p>
                <div v-else></div>
                <button
                    type="button"
                    class="font-medium transition-all duration-300 border-2 rounded-md inline-block py-2 px-3 md:px-4 bg-leonard-green border-leonard-green text-leonard-white hover:bg-leonard-green-hover hover:border-transparent focus:ring-leonard-green cursor-pointer disabled:bg-leonard-green-disabled disabled:text-leonard-green disabled:cursor-not-allowed"
                    v-if="!authnet && !internal && (isEmployee || total > 0 || (total === 0 && discount && discount > 0))"
                    :disabled="disableForms"
                    @click="checkDiscountAndProceed()"
                >
                    {{ total > 0 ? "Choose Payment" : "Continue" }}
                    <font-awesome-icon
                        v-if="disableForms"
                        icon="spinner"
                        spin
                    ></font-awesome-icon>
                </button>
            </div>
            <AuthnetPayForm
                :voucher="voucher"
                :voucher-amt="voucherAmt"
                :subtotal="subtotal"
                :discount="discount"
                :shipping-and-handling="shipping"
                :total-full="total"
                :tax="tax"
                :prefix="prefix"
                :items="items"
                :showingForm="form"
                v-on:updateCartStatus="updateCartStatus"
                v-on:cancelPayment="cancelAuthnet"
                v-if="authnet"
            ></AuthnetPayForm>
            <InternalPayForm
                v-on:updateCartStatus="updateCartStatus"
                v-on:cancelPayment="cancelInternal"
                v-if="internal"
            ></InternalPayForm>
        </div>

        <div class="data-loading text-center mt-5" v-else>
            <font-awesome-icon
                size="3x"
                icon="spinner"
                pulse
            ></font-awesome-icon>
        </div>

        <div class="modal fade" id="poses">
            <div class="modal-dialog modal-dialog-centered modal-lg">
                <div class="modal-content">
                    <div
                        class="flex items-center justify-between bg-gray-100 py-2 px-2 rounded-t-md"
                    >
                        <h4 class="m-0">
                            Preview: Chosen Poses
                        </h4>
                        <div
                            class="text-right mr-2 text-gray-700 hover:text-gray-900 hover:underline cursor-pointer lg:hidden"
                            data-dismiss="modal"
                        >
                            <font-awesome-icon :icon="['fas', 'xmark']" />
                            Close
                        </div>
                    </div>
                    <div class="modal-body">
                        <div class="flex flex-wrap items-center justify-around">
                            <div v-if="modalPoses.length < 1">
                                No previews available
                            </div>
                            <figure
                                style="width: calc(90% / 4);min-width:175px;"
                                v-for="(pose, i) in modalPoses"
                                :key="i"
                            >
                                <img
                                    class="p-2 w-full"
                                    :src="Object.values(pose)"
                                    :alt="'Pose {i}'"
                                />
                            </figure>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button
                            type="button"
                            class="bg-transparent hover:bg-gray-600 text-gray-700 hover:text-white py-1 px-4 border border-gray-600 hover:border-transparent rounded"
                            @click.prevent="closeModals"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { library } from "@fortawesome/fontawesome-svg-core";
import { faFastForward, faCircleInfo, faSpinner, faArrowsRotate, faTrash, faXmark } from "@fortawesome/free-solid-svg-icons";

import Cart from "../../classes/Cart";
import Order from "../../classes/Order";
import Photo from "../../classes/Photo";
import Package from "../../classes/Package";
import AuthorizeNet from "../../classes/AuthorizeNet";

import Discount from "./partials/Discount";
import HeadFoot from "./partials/StepHeaderFooter";
import InternalPayForm from "./partials/InternalPayForm";
import AuthnetPayForm from "./partials/AuthnetPayForm.vue";

export default {
    props: ["numSteps"],

    data() {
        return {
            Cart: new Cart(),
            Order: new Order(),
            Photo: new Photo(),
            Package: new Package(),
            AuthNet: new AuthorizeNet(),
            access_code: localStorage.getItem("access_code"),
            con_no: localStorage.getItem("con_no"),
            user_id: this.$auth.user().details.id,
            subtotal: 0.0,
            total: 0.0,
            shipping: 0.0,
            tax: 0.0,
            taxRate: 0.0,
            discount: 0.0,
            voucherAmt: 0.0,
            voucher: null,
            posePreviews: [],
            modalPoses: [],
            items: [],
            discount_id: null,
            authorize_token: null,
            deleting: null,
            rush: false,
            disableForms: false,
            disableRush: false,
            loading: true,
            form: false,
            internal: false,
            authnet: false,
            makePayment: false
        };
    },

    components: {
        Discount,
        HeadFoot,
        InternalPayForm,
        AuthnetPayForm
    },

    filters: {
        formatMoney(amount) {
            amount = parseFloat(amount);
            return amount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,");
        }
    },

    created() {
        library.add(
            faArrowsRotate,
            faCircleInfo,
            faSpinner,
            faTrash,
            faFastForward,
            faXmark
        );

        this.getCart();
    },

    mounted() {
        window.events.$emit("updateProgress");
        window.events.$on("refreshCheckout", this.getCart);
        document.title = "Checkout - " + document.title;
    },

    beforeDestroy() {
        window.events.$off("refreshCheckout");
    },

    computed: {
        prefix() {
            return this.numSteps === 3 ? "addon-" : "pkg_";
        }
    },

    methods: {
        getStudentName(item) {
            return item.student?.name ?? "Senior";
        },
        showModal(modalName, poses) {
            $("#" + modalName).modal("show");

            this.modalPoses = [];
            poses.forEach(pose => {
                this.modalPoses.push(
                    this.posePreviews.filter(
                        o => Object.keys(o).toString() === pose.toString()
                    )[0]
                );
            });
        },

        getPreview(pose) {
            const preview = this.posePreviews.find(subject => Object.keys(subject)[0] === pose[0]);
            return preview
                ? `background-image: url(${preview[pose[0]]});background-size:contain;background-position:center;`
                : "background-image: none";
        },

        async getImageUrl(fNum) {
            const film = fNum.substring(1, 6);
            const frame = fNum.substring(8, 3);
            const data = {
                con_no: this.con_no
            };

            return this.Photo.getImage(film, frame, data);
        },

        getPreviews(poses) {
            poses.forEach(pose => {
                this.getImageUrl(pose).then(response => {
                    let subject = {};
                    subject[pose] = response.data;

                    if (!this.posePreviews.some(poseObj => Object.keys(poseObj) === pose)) {
                        // if the pose is not already in the array
                        this.posePreviews.push(subject);
                    }
                });
            });
        },

        closeModals() {
            $(".modal").modal("hide");

            this.delay(() => {
                this.modalPoses = [];
            }, 300);
        },

        cancelPayment() {
            this.disableForms = false;
            this.authorize_token = null;
        },

        updateCartStatus(transaction) {
            let data = {
                order_number: transaction.transId,
                invoice_number: transaction.orderInvoiceNumber,
                charged_tax: this.tax,
                charged_shipping: this.shipping,
                charged_total: this.total,
                discount: this.discount,
                voucher_amt: this.voucherAmt,
                billing: transaction.billTo,
                shipping: transaction.shipTo
            };

            this.Cart.setCartCompletion(data, this.user_id)
                .then(response => {
                    if (!response.success) {
                        feedback(
                            "An error occurred completing the cart.",
                            "danger"
                        );
                        return;
                    }

                    if (parseFloat(self.discount) > 0) {
                        this.addUsedDiscount(response.data.cart_id);
                    }

                    return response.data.cart_id;
                })
                .then(cart_id => {
                    // Once finished updating, show the receipt.
                    this.$router.push({
                        name: "order-receipt",
                        params: {
                            transaction: transaction,
                            cart_id: cart_id
                        }
                    });
                });
        },

        addUsedDiscount(cart_id) {
            const data = {
                code_id: this.discount_id,
                user_id: this.user_id,
                cart_id: cart_id
            };

            this.Order.updateUsedDiscountCode(data)
                .then(response => {
                    if (!response.success) {
                        feedback("An error occurred updating the used discount", "danger");
                    }
                })
                .catch(error => {
                    console.error("An error occurred while updating the used discount code:", error);
                })
            ;
        },

        getCart() {
            this.form = false;
            this.disableForms = false;
            this.loading = true;
            this.authnet = false;
            this.internal = false;
            this.Cart.getDetailedCart(this.user_id, { con_no: this.con_no })
                .then(response => {
                    this.items = [];
                    this.discount = 0.0;
                    this.subtotal = 0.0;
                    this.total = 0.0;

                    response.data.items.forEach(item => {
                        this.addItem(item);
                    });
                })
                .then(() => {
                    this.checkVoucher();
                }).finally(() => {
                    window.events.$emit("finishedLoading");
                    this.loading = false;
                });
        },

        checkVoucher() {
            let data = {
                subtotal: this.subtotal
            };

            // don't apply voucher if the user is an employee
            if (this.isEmployee) {
                this.voucher = "";
                this.voucherAmt = 0.0;

                this.getShipping();
                return;
            }

            this.Cart.checkVoucher(data, this.user_id)
                .then(response => {
                    if (response.success && response.data) {
                        this.voucher = response.data.voucher;
                        this.voucherAmt = response.data.amount;
                    } else {
                        this.voucher = "";
                        this.voucherAmt = 0.0;
                    }
                })
                .catch(error => {
                    console.error("An error occurred while checking voucher eligibility:", error);
                    feedback("We are unable to verify your voucher eligibility.", "danger");
                })
                .finally(this.getShipping)
            ;
        },

        getPackageContents(package_number) {
            return this.Package.getSheets(package_number);
        },

        addItem(product) {
            this.items.push({
                id: product.id,
                student: product.student,
                package: product.package_number,
                name: product.name,
                description: product.description,
                contents: product.contents,
                price: product.price,
                tax: product.tax,
                subtotal: product.price,
                poses: product.poses,
                details: product.details,
                access_code: product.access_code,
                parent_id: product.parent_id,
                is_rush: product.is_rush,
                is_download: product.is_download,
                quantity: product.quantity
            });

            this.getPreviews(product.poses);

            this.subtotal += parseFloat(product.price);
        },

        removeItem(item, index) {
            const data = {
                user_id: this.user_id,
                item: item,
                type: "remove"
            };
            this.deleting = index;

            this.Cart.removeItem(data, data.user_id)
                .then(response => {
                    this.deletingItemIndex = null;
                    if (!response.success) return;

                    this.subtotal -= parseFloat(item.price);
                    this.tax -= parseFloat(item.tax);
                    this.calculateTotal();

                    let parentItemId = this.items[index].id;
                    this.items.reduceRight((_, currentItem, currentIndex, itemsArray) => {
                        if (currentItem.parent_id === parentItemId) {
                            itemsArray.splice(currentIndex, 1);
                        }
                    }, []);
                    this.items.splice(index, 1);
                    window.events.$emit("refreshCart");
                    this.getCart();
                })
                .catch(error => {
                    console.error('An error occurred while removing item from cart:', error);
                })
                .finally(() => {
                    this.deleting = null;
                })
            ;
        },

        getShipping() {
            const data = {
                con_no: this.con_no
            };

            // if the rush shipping is in the cart, disable the button
            this.rush =
                this.items.filter(item => item.is_rush).length > 0 ||
                this.items.filter(
                    item =>
                        item.contents.filter(cont =>
                            cont.split(" ").includes("RUSH")
                        ).length > 0
                ).length > 0;

            this.disableRush =
                this.items.filter(item => item.is_rush).length > 0 ||
                this.items.filter(
                    item =>
                        item.contents.filter(cont =>
                            cont.split(" ").includes("RUSH")
                        ).length > 0
                ).length > 0;

            // if there is only retouching in the cart, disable rush processing
            if (
                this.items.length === 1 &&
                this.items.filter(item => item.package === "038").length > 0
            ) {
                this.disableRush = true;
                this.rush = false;
            }

            // Filter items from the cart that are not digital downloads, retouching, or rushed processing
            // Skip by default - but if we have anything other than those, get the shipping cost
            // Even if we don't skip, getShipping should catch if the item shouldn't be charged for shipping
            // Package 030 is for MPL-910, and 040 is rushed processing
            let skipShipping = true;
            if (
                this.items.filter(item => {
                    return (
                        item.package !== "030" &&
                        item.package !== "038" &&
                        item.package !== "039" &&
                        item.package !== "040" &&
                        item.package !== "022" &&
                        item.package !== "023"
                    );
                }).length > 0
            ) {
                skipShipping = false;
            }

            if (skipShipping) {
                this.shipping = 0;
                this.calculateTotal();
            } else {
                this.Cart.getShipping(data)
                    .then(response => {
                        if (!response.success) return;
                        this.shipping = parseFloat(response.data.price);
                    })
                    .finally(() => {
                        this.calculateTotal();
                    });
            }
        },

        toggleRushProcess() {
            const rushEnabled = {
                true: () => {
                    this.getRushPkg();
                },
                false: () => {
                    let itemIndex = this.items.findIndex(item => item.is_rush);
                    this.removeItem(this.items[itemIndex], itemIndex);
                }
            };

            rushEnabled[this.rush.toString()]();

            this.calculateTotal();
        },

        getRushPkg() {
            const data = {
                con_no: this.con_no
            };

            this.Cart.getRushProcessing(data).then(response => {
                // enable rush based on the response from the getter
                this.rush = response.success;

                // check if the order is being rushed into processing
                if (this.rush) {
                    // add the rush processing cart item
                    this.addToCart(response.data);

                    // hide rush processing control on the checkout page when processing has been added
                    // for the student.
                    this.disableRush = true;
                } else {
                    // if the rush processing was not added, show an error message
                    feedback(
                        "We are unable to rush processing for this order.",
                        "danger"
                    );
                }
            });
        },

        addToCart(addon) {
            const data = {
                user_id: this.user_id,
                name: addon.flyername,
                con_no: this.con_no,
                school: this.school,
                accesscode: this.access_code,
                price: addon.price,
                tax_amt: addon.tax_amt,
                extra_poses: 0,
                package_number: addon.pkg_no,
                contents: [],
                is_rush: addon.is_rush,
                is_download: addon.is_download
            };

            this.Cart.addItem(data, data.user_id)
                .then(() => {
                    window.events.$emit("addToCart");
                    this.getCart();
                })
                .catch(error => {
                    console.error('An error occurred while adding to cart:', error);
                });
        },

        applyDiscount(amount, amountType, shipping_only, id) {
            const data = {
                discount_id: id
            };

            this.Cart.addDiscount(data, this.user_id).then(response => {
                if (!response.success) {
                    feedback("unable to add discount", "danger");
                    return;
                }

                let discounts = {
                    dollar: amount,
                    percentage: (parseInt(amount) / 100) * this.subtotal
                };

                this.discount_id = id;
                this.discount = !shipping_only ? discounts[amountType] : 0.0;

                if (amountType === "percentage") {
                    if (parseInt(amount) === 100) {
                        this.shipping = 0;
                    }
                    if (shipping_only && this.shipping !== 0.0) {
                        this.shipping = this.shipping - (parseInt(amount) / 100) * this.shipping;
                    }
                }

                this.calculateTotal();
            });
        },

        removeDiscount() {
            this.Cart.removeDiscount(this.user_id).then(response => {
                if (!response.success) {
                    feedback("unable to remove discount", "danger");
                    return;
                }

                this.discount = 0;
                this.getShipping();
            });
        },

        calculateTotal() {
            this.makePayment = false;
            this.calculateTax().finally(() => {
                let val = this.subtotal + this.shipping - this.discount - this.voucherAmt + this.tax;
                this.total = val > 0 ? val : 0;

                this.makePayment = true;
            });
        },

        showInternal() {
            this.form = true;
            this.internal = true;
            this.authnet = false;
        },

        showAuthnet() {
            this.form = true;
            this.internal = false;
            this.authnet = true;
            this.$refs["checkout-main"].scrollIntoView({ behavior: "smooth" });
        },

        cancelInternal() {
            this.form = false;
            this.internal = false;
            this.disableForms = false;
        },

        cancelAuthnet() {
            this.authnet = false;
            this.disableForms = false;
            this.form = false;
        },

        async calculateTax() {
            const data = {
                con_no: this.con_no
            };

            await this.Cart.getTax(data).then(response => {
                if (!response.success) return;

                let val = (this.subtotal + this.shipping - this.discount - this.voucherAmt) * response.data.taxRate;
                this.tax = val > 0 ? val : 0;
            });
        },

        checkPrices() {
            const data = {
                subtotal: this.subtotal,
                discount: this.discount,
                voucher: this.voucher,
                voucher_amt: this.voucherAmt,
                shipping: this.shipping,
                tax: this.tax,
                total: this.total
            };

            this.Cart.checkPrices(data, this.user_id).then(response => {
                if (!response.success || !response.data.valid) {
                    this.disableForms = false;
                    feedback(
                        "Unable to verify totals. Please try again.",
                        "danger"
                    );
                    return;
                }
                this.total > 0 ? this.showAuthnet() : this.showInternal();
            });
        },

        async checkDiscountAndProceed() {
            this.disableForms = true;
            await this.$refs.discountComponent.triggerDiscountCheck();

            if (!this.$refs.discountComponent.expired) {
                this.next();
            } else {
                this.disableForms = false;
            }
        },

        next() {
            this.disableForms = true;
            this.checkPrices();
        },

        back() {
            this.$emit("back", "addons");
        }
    }
};
</script>

<style lang="scss" scoped>
.pkg_img {
    background-image: url("/images/Master.png");
    width: 180px;
    height: 180px;
    background-size: 1200px;

    &.pkg_008 {
        // 20x24 Gallery Wrap
        background-position: 690px 340px;
    }

    &.pkg_009 {
        // 16x20 Gallery Wrap
        background-position: 855px 340px;
    }

    &.pkg_077 {
        // 11x14 Gallery Wrap
        background-position: 335px 656px;
    }

    &.addon-001,
    &.pkg_010 {
        // 20x24
        background-position: 337px -165px;
    }

    &.addon-002,
    &.pkg_011 {
        // 16x20
        background-position: 185px -170px;
    }

    &.addon-003,
    &.pkg_012 {
        // 11x14
        background-position: -5px -170px;
    }

    &.addon-004,
    &.pkg_013 {
        // 8x10
        background-size: 1400px;
        background-position: 1180px -215px;
    }

    &.addon-005,
    &.pkg_014 {
        // 5x7
        background-size: 1400px;
        background-position: 995px -205px;
    }

    &.addon-006,
    &.pkg_015 {
        // 4x5
        background-size: 1400px;
        background-position: 780px -215px;
    }

    &.addon-007,
    &.pkg_016 {
        // Wallet
        background-size: 1400px;
        background-position: 595px -205px;
    }

    &.pkg_020 {
        // Preview Book
        background-size: 2000px;
        background-position: -1200px -860px;
    }

    &.pkg_018,
    &.pkg_019 {
        // Standard Portfolio & Previews
        background-position: 340px 495px;
    }

    &.pkg_021 {
        // DVD Slideshow
        background-size: 1800px;
        background-position: -1337px -768px;
    }

    &.pkg_C02 {
        // The Composite
        background-size: 1500px;
        background-position: -10px -475px;
    }

    &.pkg_C04 {
        // The Collection
        background-size: 1500px;
        background-position: -440px -475px;
    }

    &.pkg_C06 {
        // The Charmed
        background-size: 1500px;
        background-position: -670px -475px;
    }

    &.pkg_C08 {
        // The Vestige
        background-size: 1400px;
        background-position: -215px -450px;
    }

    &.pkg_C10 {
        // The Journey
        background-size: 1500px;
        background-position: -870px -475px;
    }

    &.addon-023,
    &.addon-I07,
    &.pkg_022 {
        // Hi-Res Download
        background-position: 515px 185px;
    }

    &.pkg_P03 {
        // Elite Portfolio
        background-position: 173px 494px;
    }

    &.pkg_P04 {
        // Custom Portfolio
        background-position: 5px 350px;
    }

    &.pkg_P05 {
        // Standard Portfolio
        background-position: -170px 340px;
    }

    &.pkg_001 {
        // Supermodel
        background-position: 5px 5px;
    }

    &.pkg_002 {
        // Signature
        background-position: 1030px 2px;
    }

    &.pkg_003 {
        // Celebrity
        background-position: 855px 4px;
    }

    &.pkg_004 {
        // A-List
        background-position: 688px 0;
    }

    &.pkg_005 {
        // Rockstar
        background-position: 515px -5px;
    }

    &.pkg_006 {
        // Graduate
        background-position: 335px -5px;
    }

    &.pkg_007 {
        // Trendsetter
        background-position: 180px 0;
    }
}

.fa-info-circle {
    cursor: pointer;
}

// Fix for Safari
.item-wrapper .row {
    margin: 0;
}
</style>
